import { Button, IconButton, Typography, useBreakpoint } from "@povio/ui";
import clsx from "clsx";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import type { CardComponentProps } from "nextstepjs";
import { useEffect, useRef, useState } from "react";

import { ChevronLeftIcon } from "@/assets/icons/general/ChevronLeft";
import { ChevronRightIcon } from "@/assets/icons/general/ChevronRight";
import { CloseIcon } from "@/assets/icons/general/CloseIcon";
import { DogHexagonFrameIcon } from "@/assets/icons/general/DogHexagonFrame";
import { DogTourPointerIcon } from "@/assets/icons/general/DogTourPointer";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { MobileTourCard } from "@/components/ui/tours/TourCard/MobileTourCard";
import { AuthContext } from "@/data/auth/auth.context";
import { useDynamicTourContent } from "@/hooks/useDynamicTourContent";
import AdogExitAnimation from "@/lottie/AdogExit.json";
import AdogIntroAnimation from "@/lottie/AdogIntro.json";
import AdogTalkingAnimation from "@/lottie/AdogTalking.json";

type AnimationPhase = "intro" | "talking" | "exit";

export const TourCard = (props: CardComponentProps) => {
  const { step, currentStep, totalSteps, nextStep, prevStep, skipTour } = props;
  const isDesktop = useBreakpoint("md");

  // Render mobile tour card on smaller screens
  if (!isDesktop) {
    return <MobileTourCard {...props} />;
  }

  const { user, isLoggedIn } = AuthContext.useAuth();
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // State for tracking animation phase and completing state
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>(isFirstStep ? "intro" : "talking");
  const [isClosing, setIsClosing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const shouldShowCloseButton = user && isLoggedIn;

  // Track if intro animation has already been shown (to prevent replay on navigation back)
  const hasShownIntro = useRef(false);

  // Reset to intro animation when returning to first step
  useEffect(() => {
    if (isFirstStep && !isClosing && !isCompleting) {
      setAnimationPhase("intro");
    }
  }, [isFirstStep, isClosing, isCompleting]);

  // Set animation direction based on phase
  useEffect(() => {
    if (lottieRef.current) {
      if (animationPhase === "intro") {
        // Play in reverse for intro (scale up effect)
        lottieRef.current.setDirection(-1);
      } else {
        // Play forward for talking and exit
        lottieRef.current.setDirection(1);
      }
    }
  }, [animationPhase]);

  const dynamicContent = useDynamicTourContent(step);

  if (!step) return null;

  // Handle intro animation completion - transition to talking
  const handleIntroComplete = () => {
    if (animationPhase === "intro") {
      hasShownIntro.current = true;
      setAnimationPhase("talking");
    }
  };

  // Handle close/skip button click - start exit animation
  const handleClose = () => {
    setIsClosing(true);
    setAnimationPhase("exit");
  };

  // Handle exit animation completion - close or complete the tour
  const handleExitComplete = () => {
    if (isCompleting && nextStep) {
      // Completing the tour - advance to next step (which will mark tour as complete)
      nextStep();
    } else if (isClosing && skipTour) {
      // Closing/skipping the tour
      skipTour();
    }
  };

  // Handle complete button click - start exit animation then complete tour
  const handleComplete = () => {
    setIsCompleting(true);
    setAnimationPhase("exit");
  };

  // Get current animation data
  const getCurrentAnimation = () => {
    switch (animationPhase) {
      case "intro":
        return AdogIntroAnimation;
      case "talking":
        return AdogTalkingAnimation;
      case "exit":
        return AdogExitAnimation;
      default:
        return AdogTalkingAnimation;
    }
  };

  return (
    <div
      className="relative z-9999 flex items-center"
      data-tour-card="true"
    >
      {/* Dog Character */}
      <div className="relative shrink-0">
        {/* Hexagon Frame Background */}
        <DogHexagonFrameIcon className={clsx("h-[362px] w-[326px] opacity-0")} />

        {/* Lottie Animation Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Lottie
            lottieRef={lottieRef}
            animationData={getCurrentAnimation()}
            loop={animationPhase === "talking"}
            autoplay
            onComplete={animationPhase === "intro" ? handleIntroComplete : handleExitComplete}
            className="h-[300px] w-[300px] origin-center translate-x-12 scale-[200%] will-change-transform"
            style={{
              // Safari-specific optimizations for crisp scaling
              WebkitFontSmoothing: "antialiased",
              imageRendering: "-webkit-optimize-contrast",
            }}
            {...({
              // Safari rendering optimizations - use canvas with high DPI BEFORE scaling
              // This renders at 2x resolution first, then the scale-[200%] enlarges it
              renderer: "canvas",
              rendererSettings: {
                clearCanvas: true,
                progressiveLoad: false,
                // High DPI rendering ensures crisp result even after CSS scaling
                dpr: typeof window !== "undefined" ? (window.devicePixelRatio || 2) * 2 : 4,
              },
            } as any)}
          />
        </div>
      </div>

      {/* Speech Bubble Container */}
      <div className="relative flex -translate-x-14 -translate-y-7 flex-col">
        {/* Pointer/Arrow - points left toward the dog */}
        <div className="absolute bottom-0 left-[-24px]">
          <DogTourPointerIcon className="w-[57px] text-elevation-fill-inverted-0" />
        </div>

        {/* Speech Bubble */}
        <div className="relative flex w-[289px] flex-col gap-6 rounded-tl-[22px] rounded-tr-[22px] rounded-br-[22px] bg-elevation-fill-inverted-0 p-6">
          {shouldShowCloseButton && (
            <InlineIconButton
              label="close"
              icon={CloseIcon}
              onPress={handleClose}
              iconClassName="w-6 h-6 text-black hidden"
              className="absolute top-2 right-2 hidden"
            />
          )}
          {/* Content - Using arbitrary value to match Figma specs (24px/26px) */}
          <Typography
            size="body-demo"
            className="text-text-inverted-2"
          >
            {dynamicContent}
          </Typography>

          <div className="flex items-center gap-6">
            {/* Back Button */}
            {!isFirstStep && (
              <IconButton
                label="Back"
                size="s"
                color="secondary"
                disableTooltip
                className="bg-transparent text-text-inverted-2!"
                variant="outlined"
                icon={ChevronLeftIcon}
                onPress={prevStep}
              />
            )}

            <Button
              variant="outlined"
              color="secondary"
              size="s"
              iconPosition="right"
              icon={ChevronRightIcon}
              className="bg-transparent text-text-inverted-2!"
              onPress={isLastStep ? handleComplete : nextStep}
            >
              {isLastStep ? "Finish" : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
